/*
 * Copyright 2025 ixPilot Teknoloji A.Ş. (ixpilot2021@gmail.com)
 * All rights reserved.
 *
 * This software is proprietary and confidential.
 * Unauthorized copying, modification, or distribution of this software,
 * via any medium, is strictly prohibited.
 *
 * Developed and maintained by ixPilot Teknoloji A.Ş.
 */

/**
 * Merkezi Error Handling Utility
 *
 * apiFetch ile gelen hatalarda mümkünse backend `detail` (UETDS SOAP fault dahil) gösterilir;
 * özü çıkarılamazsa HTTP durumu için kısa mesaj kullanılır.
 */

const isDevelopment = process.env.NODE_ENV === "development";

/**
 * HTTP status code'a göre user-friendly mesaj döndürür
 */
const getErrorMessageByStatus = (status) => {
  const statusMessages = {
    400: "Geçersiz istek. Lütfen girdiğiniz bilgileri kontrol edin.",
    401: "Oturum süreniz dolmuş. Lütfen tekrar giriş yapın.",
    403: "Bu işlem için yetkiniz bulunmamaktadır.",
    404: "İstenen kayıt bulunamadı.",
    409: "Bu işlem için çakışma oluştu. Lütfen tekrar deneyin.",
    422: "Gönderilen veriler geçersiz. Lütfen kontrol edin.",
    429: "Çok fazla istek gönderildi. Lütfen bir süre sonra tekrar deneyin.",
    500: "Sunucu hatası oluştu. Lütfen daha sonra tekrar deneyin.",
    502: "Sunucuya bağlanılamıyor. Lütfen daha sonra tekrar deneyin.",
    503: "Servis şu anda kullanılamıyor. Lütfen daha sonra tekrar deneyin.",
    504: "Sunucu yanıt vermiyor. Lütfen daha sonra tekrar deneyin.",
  };
  
  return statusMessages[status] || "Bir hata oluştu. Lütfen daha sonra tekrar deneyin.";
};

/** Traccar / UETDS hatalarında SOAP fault veya özeti kullanıcıya gösterilebilir kısaltır. */
export const sanitizeBackendDetailForDisplay = (detail) => {
  if (detail == null || detail === "") return null;

  let s =
    typeof detail === "object" &&
    detail !== null &&
    typeof detail.msg !== "string" &&
    !Array.isArray(detail)
      ? JSON.stringify(detail)
      : String(
          typeof detail === "object" && detail !== null && typeof detail.msg === "string"
            ? detail.msg
            : detail
        );
  s = s.trim();
  if (!s) return null;

  if (/traceback\s*\(/i.test(s) || /\n\s*File "[^"]+\.py", line \d+/.test(s)) {
    return null;
  }

  const xmlMarker = s.search(/<\?xml|<\s*soapenv\s*:[^>]*Envelope|<\s*soap\s*:[^>]*Envelope/i);
  const xmlBlob = xmlMarker >= 0 ? s.slice(xmlMarker) : s;

  const faultStrM = xmlBlob.match(/<faultstring>\s*([\s\S]*?)\s*<\/faultstring>/i);
  const faultDetM = xmlBlob.match(/<detail>\s*([\s\S]*?)\s*<\/detail>/i);
  const fStr = faultStrM ? faultStrM[1].trim().replace(/\s+/g, " ") : "";
  const fDet = faultDetM ? faultDetM[1].trim().replace(/\s+/g, " ") : "";
  if (fStr || fDet) {
    if (fStr && fDet) return `${fStr}: ${fDet}`;
    return fStr || fDet;
  }

  const beforeStack = s.split(/\r?\n\s*at |\n\tat |\tat org\.(?:traccar|java)/i)[0].trim();
  let head = (beforeStack || s).replace(/\|\s+/g, " — ").trim();
  const MAX = 1200;
  if (head.length > MAX) head = `${head.slice(0, MAX - 1)}…`;
  return head || null;
};

/** FastAPI 422 doğrulama gövdesi */
const summarizeValidationErrors = (arr) => {
  if (!Array.isArray(arr) || arr.length === 0) return null;
  const parts = arr.map((x) => {
    if (x && typeof x === "object" && typeof x.msg === "string") {
      const locStr = Array.isArray(x.loc) ? x.loc.join(".") : "";
      return `${locStr ? `[${locStr}] ` : ""}${x.msg}`.trim();
    }
    try {
      return JSON.stringify(x);
    } catch {
      return "";
    }
  }).filter(Boolean);
  if (!parts.length) return null;
  const j = parts.join("; ");
  return j.length > 1500 ? `${j.slice(0, 1480)}…` : j;
};

/**
 * Error'dan kullanıcıya gösterilecek metin çıkarır.
 */
export const getErrorMessage = (error, context = '') => {
  if (isDevelopment) {
    console.error(`[Error Handler] ${context}:`, error);
  }

  if (!error) {
    return "Bilinmeyen bir hata oluştu.";
  }

  if (error.name === "TypeError" && error.message?.includes("fetch")) {
    return "Sunucuya bağlanılamıyor. İnternet bağlantınızı kontrol edin.";
  }

  if (error.name === "SyntaxError" && error.message?.includes("JSON")) {
    return "Sunucudan gelen veri işlenemedi. Lütfen daha sonra tekrar deneyin.";
  }

  const rawDetail =
    typeof error?.data?.detail !== "undefined" ? error.data.detail : undefined;
  const alsoMessage =
    error instanceof Error && error.message?.trim?.() !== "" ? error.message : null;

  if (Array.isArray(rawDetail)) {
    const summarized = summarizeValidationErrors(rawDetail);
    if (summarized) return summarized;
  }

  if (typeof rawDetail === "string") {
    const cleaned = sanitizeBackendDetailForDisplay(rawDetail);
    if (cleaned) return cleaned;
  }

  if (alsoMessage && alsoMessage !== rawDetail) {
    const cleanedMsg = sanitizeBackendDetailForDisplay(alsoMessage);
    if (cleanedMsg) return cleanedMsg;
  }

  if (
    typeof error === "object" &&
    error != null &&
    typeof error.detail === "string" &&
    typeof error.status === "undefined"
  ) {
    const cleaned = sanitizeBackendDetailForDisplay(error.detail);
    if (cleaned) return cleaned;
  }

  // HTTP özeti — ayrıntı yukarıda tüketildi
  if (error.status !== undefined && error.status !== null && error.status !== "") {
    const n = typeof error.status === "number" ? error.status : parseInt(error.status, 10);
    if (!Number.isNaN(n)) {
      return getErrorMessageByStatus(n);
    }
  }

  if (error instanceof Error) {
    const message = error.message || "";
    if (message.includes("Oturum süresi doldu")) return message;

    const low = message.toLowerCase();
    const safeHints = ["yetkiniz yok", "bulunamadı", "geçersiz", "eksik", "zorunlu"];
    if (safeHints.some((h) => low.includes(h)) && message.length < 600) {
      return message;
    }
    if ((message.includes("yetki") || message.includes("servis")) && message.length < 800) {
      return message;
    }
  }

  if (typeof error === "string") {
    const cleaned = sanitizeBackendDetailForDisplay(error);
    if (cleaned) return cleaned;
    return error;
  }

  return "Bir hata oluştu. Lütfen daha sonra tekrar deneyin.";
};

/**
 * API response'u kontrol eder ve error fırlatır
 */
export const handleApiResponse = async (response, context = '') => {
  // 401 Unauthorized - Oturum süresi dolmuş
  if (response.status === 401) {
    // Detaylı error bilgisini SADECE development'ta console'a yaz
    if (isDevelopment) {
      console.error('[Error Handler] Unauthorized:', {
        url: response.url,
        status: response.status,
        context,
      });
    }
    
    // Login sayfasındayken redirect yapma (sonsuz döngü önleme)
    const isLoginPage = window.location.hash === '#/login' || window.location.pathname === '/login';
    if (!isLoginPage) {
      window.location.href = "/login";
    }
    throw new Error("Oturum süresi doldu");
  }
  
  // Diğer hata durumları
  if (!response.ok) {
    let errorData;
    try {
      errorData = await response.json();
    } catch {
      // JSON parse edilemezse text olarak al
      const text = await response.text();
      errorData = { detail: text || `HTTP ${response.status}: ${response.statusText}` };
    }
    
    // Detaylı error bilgisini SADECE development'ta console'a yaz
    if (isDevelopment) {
      console.error('[Error Handler] API Error:', {
        url: response.url,
        status: response.status,
        statusText: response.statusText,
        errorData,
        context,
      });
    }
    
    // Error object oluştur (status bilgisi ile)
    const error = new Error(errorData?.detail || errorData?.message || getErrorMessageByStatus(response.status));
    error.status = response.status;
    error.data = errorData;
    throw error;
  }
  
  return response;
};

/**
 * Try-catch wrapper - Otomatik error handling
 */
export const safeApiCall = async (apiCall, context = '', options = {}) => {
  const { 
    onError = null, // Custom error handler
    showToast = null, // Toast function (opsiyonel)
    defaultMessage = null, // Custom default message
  } = options;
  
  try {
    const response = await apiCall();
    return await handleApiResponse(response, context);
  } catch (error) {
    const userMessage = defaultMessage || getErrorMessage(error, context);
    
    // Custom error handler varsa çağır
    if (onError) {
      onError(error, userMessage);
      return null;
    }
    
    // Toast göster (varsa)
    if (showToast) {
      showToast({
        title: "Hata",
        description: userMessage,
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    }
    
    // Error'ı tekrar fırlat (component'ler kendi handling yapabilir)
    throw new Error(userMessage);
  }
};

const errorHandler = {
  getErrorMessage,
  handleApiResponse,
  safeApiCall,
};

export default errorHandler;
